import { apiRequest } from './client';

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
  parentId: string;
};

// Parent profile shape returned by /v1/auth/me. Same shape comes back
// from PATCH /me. Password + PIN hashes are stripped server-side.
export type ParentProfile = {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  // True when this account is in the server's SUPER_ADMIN_EMAILS allowlist.
  // Drives admin-only UI (e.g. approving a parent's video for the public
  // library). Optional for back-compat with older servers.
  isSuperAdmin?: boolean;
};

export const authApi = {
  signup: (email: string, password: string, name?: string) =>
    apiRequest<AuthTokens>('/v1/auth/signup', {
      method: 'POST',
      body: { email, password, name },
    }),

  login: (email: string, password: string) =>
    apiRequest<AuthTokens>('/v1/auth/login', {
      method: 'POST',
      body: { email, password },
    }),

  // Exchange a Google ID token (from Google Identity Services) for app
  // tokens. The backend verifies the token and links/creates the parent by
  // its verified email.
  google: (idToken: string) =>
    apiRequest<AuthTokens>('/v1/auth/google', {
      method: 'POST',
      body: { idToken },
    }),

  refresh: (token: string) =>
    apiRequest<AuthTokens>('/v1/auth/refresh', {
      method: 'POST',
      token,
    }),

  // Email a password-reset link. Always resolves ok (the backend never
  // reveals whether the email is registered).
  requestPasswordReset: (email: string) =>
    apiRequest<{ ok: boolean }>('/v1/auth/password/forgot', {
      method: 'POST',
      body: { email },
    }),

  // Redeem the emailed 6-digit code + set a new password (all in-app).
  // Returns fresh tokens so we can sign the parent straight in.
  resetPassword: (email: string, code: string, password: string) =>
    apiRequest<AuthTokens>('/v1/auth/password/reset', {
      method: 'POST',
      body: { email, code, password },
    }),

  // Permanently delete the parent's account and all data. Caller signs out
  // after this resolves.
  deleteAccount: (token: string) =>
    apiRequest<{ ok: boolean }>('/v1/auth/me', {
      method: 'DELETE',
      token,
    }),

  setPin: (pin: string, token: string) =>
    apiRequest<{ ok: boolean }>('/v1/auth/pin/set', {
      method: 'POST',
      body: { pin },
      token,
    }),

  verifyPin: (pin: string, token: string) =>
    apiRequest<{ ok: boolean }>('/v1/auth/pin/verify', {
      method: 'POST',
      body: { pin },
      token,
    }),

  changePin: (currentPin: string, newPin: string, token: string) =>
    apiRequest<{ ok: boolean }>('/v1/auth/pin/change', {
      method: 'POST',
      body: { currentPin, newPin },
      token,
    }),

  // Change password while signed in (current + new). Forgotten passwords use
  // requestPasswordReset + resetPassword instead.
  changePassword: (currentPassword: string, newPassword: string, token: string) =>
    apiRequest<{ ok: boolean }>('/v1/auth/password/change', {
      method: 'POST',
      body: { currentPassword, newPassword },
      token,
    }),

  // "Forgot PIN": set a new PIN by re-authenticating with the account
  // password (no old PIN needed). The parent is already signed in.
  resetPin: (password: string, newPin: string, token: string) =>
    apiRequest<{ ok: boolean }>('/v1/auth/pin/reset', {
      method: 'POST',
      body: { password, newPin },
      token,
    }),

  pinStatus: (token: string) =>
    apiRequest<{ hasPin: boolean }>('/v1/auth/pin/status', { token }),

  // Fetch the calling parent's profile. Used by WebFamilyAccount to show
  // the real name + email without JWT-decoding tricks.
  me: (token: string) =>
    apiRequest<ParentProfile>('/v1/auth/me', { token }),

  // Patch the parent's editable fields. Today only `name` is patchable;
  // email changes need a dedicated re-auth flow we haven't built yet.
  updateMe: (
    body: { name?: string | null },
    token: string,
  ) =>
    apiRequest<ParentProfile>('/v1/auth/me', {
      method: 'PATCH',
      body,
      token,
    }),
};
